import React, { useState } from 'react';
import Button from '../../../../Form/Button/Button';
import Select from '../../../../Form/Select/Select';
import ThumbsDownIcon from '../../../../../assets/images/Thumbs Down.svg';
import ThumbsUpIcon from '../../../../../assets/images/Thumbs Up.svg';
import IsaTransferInstructionsModal from '../../DISA/IsaTransferInstructionsModal';
import { fisaPenaltyClosureReasons } from './fisaPenaltyClosureReasons';
import { useUnifiedAnalytics } from '../../../../../contexts/AnalyticsWrapper';
import { useLogger } from '../../../../../contexts/Logger';
import './FisaPenaltyNoticeStepsOrchestrator.scss';

/** @import { OptionType } from '../../../../Form/Select/SelectTypes' */

/**
 * FISA Penalty Notice Single Page Form
 * Displays all sections conditionally based on user selections
 * @param {Object} props
 * @param {Object} props.fisaAccount - The FISA account data
 * @param {Function} props.onCloseAccount - Handler for closing the account (triggers OTP)
 * @param {Function} props.onClosureDataChange - Handler for when closure data changes
 */
function FisaPenaltyNoticeStepsOrchestrator({ fisaAccount, onCloseAccount, onClosureDataChange }) {
    const analytics = useUnifiedAnalytics();
    const logger = useLogger();
    const [currentStep, setCurrentStep] = useState('form'); // 'form' or 'confirmation'
    const [selectedReason, setSelectedReason] = useState(/** @type {string | null} */(null));
    const [closureReason, setClosureReason] = useState(/** @type {OptionType | null} */(null));
    const [freeTextReason, setFreeTextReason] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [payoutOption, setPayoutOption] = useState(''); // No default selection
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [penaltyAcknowledged, setPenaltyAcknowledged] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);

    /**
     * Format number with commas and proper decimal places
     * @param {number} amount - The amount to format
     * @returns {string} Formatted amount string with £ symbol
     */
    const formatCurrency = (amount) => {
        return `£${amount.toLocaleString('en-GB', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    };

    /**
     * Calculate payment details using backend closure fee
     * @returns {Object} Payment calculation details
     */
    const calculatePaymentDetails = () => {
        if (!fisaAccount) {
            return {
                currentBalance: 0,
                earlyClosureFee: 0,
                finalPayment: 0,
                formattedCurrentBalance: '£0.00',
                formattedEarlyClosureFee: '£0.00',
                formattedFinalPayment: '£0.00'
            };
        }

        // Safely extract balance and accrued interest with fallbacks
        const balanceStr = (fisaAccount && typeof fisaAccount === 'object' && 'balance' in fisaAccount) 
            ? String(fisaAccount.balance || '0') 
            : '0';
        const interestAccruedStr = (fisaAccount && typeof fisaAccount === 'object' && 'interestAccrued' in fisaAccount) 
            ? String(fisaAccount.interestAccrued || '0') 
            : '0';
        
        const baseBalance = parseFloat(balanceStr) || 0;
        const interestAccrued = parseFloat(interestAccruedStr) || 0;
        
        // Calculate total balance including accrued interest
        const totalBalance = baseBalance + interestAccrued;
        
        // Use the backend-calculated closure fee instead of manual calculation
        const closureFee = (fisaAccount && typeof fisaAccount === 'object' && 'closureFee' in fisaAccount) 
            ? parseFloat(String(fisaAccount.closureFee || '0')) || 0
            : 0;
        
        const finalPayment = Math.max(0, totalBalance - closureFee);

        return {
            currentBalance: totalBalance,
            earlyClosureFee: closureFee,
            finalPayment: finalPayment,
            formattedCurrentBalance: formatCurrency(totalBalance),
            formattedEarlyClosureFee: formatCurrency(closureFee),
            formattedFinalPayment: formatCurrency(finalPayment)
        };
    };

    const paymentDetails = calculatePaymentDetails();

    // Use imported closure reason options
    const closureReasonOptions = fisaPenaltyClosureReasons;

    const handleReasonChange = (/** @type {string} */ reason) => {
        logger.info(`User selected ${reason === 'exceptional' ? 'exceptional circumstances' : 'other reason'} for FISA penalty closure`, {
            additionalData: {
                accountId: (fisaAccount && typeof fisaAccount === 'object' && 'id' in fisaAccount) ? fisaAccount.id : (fisaAccount && typeof fisaAccount === 'object' && 'accountId' in fisaAccount) ? fisaAccount.accountId : null,
                reason: reason,
                closureType: 'penalty'
            }
        });

        analytics.trackAnalyticsClickEvent(
            reason === 'exceptional' ? 'fisa penalty exceptional circumstances yes' : 'fisa penalty exceptional circumstances no',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'radio button click',
            `close_fisa_account|radio_click|${reason === 'exceptional' ? 'exceptional_circumstances' : 'other_reason'}`,
            'account_closure_penalty'
        );
        
        setSelectedReason(reason);
        // Reset dependent fields when changing exceptional circumstances answer
        if (reason === 'exceptional') {
            setClosureReason(null);
            setFreeTextReason('');
            setPayoutOption(''); // Reset to no selection
        }
        setPenaltyAcknowledged(false); // Reset penalty acknowledgment
        setShowValidation(false);
    };

    /**
     * Handle closure reason selection change
     * @param {import('react-select').ValueType<OptionType, false>} value - Selected option
     * @param {import('react-select').ActionMeta<OptionType>} _action - Action metadata
     */
    const handleClosureReasonChange = (value, _action) => {
        if (value) {
            analytics.trackAnalyticsClickEvent(
                `fisa penalty closure reason ${value.label}`,
                window.location.href,
                'fisa-penalty-closure',
                'fisa_1yr',
                '',
                {},
                'dropdown selection',
                `close_fisa_account|dropdown_click|${value.value}`,
                'account_closure_penalty'
            );
        }
        
        setShowValidation(false);
        setClosureReason(value || null);
        // Clear free text when changing reason
        if (value && value.value !== 'other' && value.value !== 'unhappy_with_service') {
            setFreeTextReason('');
        }
    };

    const handleFreeTextChange = (/** @type {string} */ text) => {
        setFreeTextReason(text);
    };

    const handlePayoutChange = (/** @type {string} */ option) => {
        analytics.trackAnalyticsClickEvent(
            `fisa penalty payout option ${option === 'linked' ? 'linked account' : 'transfer to ISA'}`,
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'radio button click',
            `close_fisa_account|radio_click|payout_${option}`,
            'account_closure_penalty'
        );
        
        setPayoutOption(option);
        // Reset penalty acknowledgment when changing payout options  
        setPenaltyAcknowledged(false);
    };

    const handleCancel = () => {
        analytics.trackAnalyticsClickEvent(
            'fisa penalty closure cancel',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|cancel',
            'account_closure_penalty'
        );
        
        window.history.back();
    };

    const handleStartLiveChat = () => {
        // Match HelpSection: use Microsoft Omnichannel SDK if available, else show fallback
        /** @type {any} */
        const globalWindow = window;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (globalWindow.Microsoft && globalWindow.Microsoft.Omnichannel && globalWindow.Microsoft.Omnichannel.LiveChatWidget) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                globalWindow.Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            } catch (error) {
                console.error('Error starting LiveChat:', error);
                alert('Unable to start live chat at this time. Please try again later or contact us via email.');
            }
        } else {
            // Fallback - LiveChat widget not loaded yet or unavailable
            alert('Live chat is currently unavailable. Please try again later or contact us via email.');
        }
        // Analytics tracking for live chat start
        analytics.trackAnalyticsClickEvent(
            'fisa penalty exceptional circumstances live chat',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|live_chat',
            'account_closure_penalty'
        );
    };

    const handleContinue = () => {
        // Validation
        if (!selectedReason) {
            setShowValidation(true);
            return;
        }

        if (selectedReason === 'other' && !closureReason) {
            setShowValidation(true);
            return;
        }

        if (selectedReason === 'other' && closureReason && 
            (closureReason.value === 'other' || closureReason.value === 'unhappy_with_service') && 
            !freeTextReason.trim()) {
            setShowValidation(true);
            return;
        }

        // Validate payout option is selected for "other" path
        if (selectedReason === 'other' && closureReason && !payoutOption) {
            setShowValidation(true);
            return;
        }

        // Validate penalty acknowledgment is checked when transfer option is selected
        if (selectedReason === 'other' && closureReason && payoutOption === 'transfer' && !penaltyAcknowledged) {
            setShowValidation(true);
            return;
        }

        // Track analytics for continue action
        analytics.trackAnalyticsClickEvent(
            'fisa penalty continue to confirmation',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|continue_to_confirmation',
            'account_closure_penalty'
        );
        
        // Show transfer instructions modal if transfer option is selected
        if (selectedReason === 'other' && closureReason && payoutOption === 'transfer') {
            setShowTransferModal(true);
            return;
        }

        // Navigate to confirmation step for all flows (including after viewing transfer modal)
        setCurrentStep('confirmation');
    };

    const handleConfirmationBack = () => {
        analytics.trackAnalyticsClickEvent(
            'fisa penalty confirmation back',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|Don’t_close_my_account',
            'account_closure_penalty'
        );
        
        setCurrentStep('form');
    };

    const handleTransferModalClose = () => {
        setShowTransferModal(false);
    };

    const handleTransferModalConfirm = () => {
        setShowTransferModal(false);
        // Don't progress to confirmation step - just close modal and stay on current form
    };

    const handleFinalSubmit = () => {
        if (!confirmationChecked) {
            setShowValidation(true);
            return;
        }

        // Track analytics for final account closure submission
        analytics.trackAnalyticsClickEvent(
            'fisa penalty close my account final',
            window.location.href,
            'fisa-penalty-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|close_account_final',
            'account_closure_penalty'
        );

        // Store closure data for later use in parent component
        const closureData = {
            selectedReason,
            closureReason,
            freeTextReason,
            payoutOption: selectedReason === 'other' ? payoutOption : null,
            confirmationChecked,
            penaltyAmount: /** @type {any} */(paymentDetails).earlyClosureFee,
            timestamp: new Date().toISOString()
        };
        
        // Pass closure data to parent component
        if (onClosureDataChange) {
            onClosureDataChange(closureData);
        }
        
        // Trigger OTP flow in parent component
        if (onCloseAccount) {
            onCloseAccount();
        }
    };

    const isOther = selectedReason === 'other';
    const shouldShowFreeText = closureReason && 
        (closureReason.value === 'other' || closureReason.value === 'unhappy_with_service');
    const shouldShowPayoutOptions = isOther && closureReason; // Only show payout after reason is selected

    return (
        <div className="fisa-penalty-notice-steps-orchestrator">
            <div className="fisa-penalty-notice-steps-orchestrator__container">
                
                {currentStep === 'form' && (
                    <div className="fisa-penalty-notice-steps-orchestrator__content">
                        
                        {/* Exceptional Circumstances Section */}
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__question fisa-penalty-notice-steps-orchestrator__question--first">
                                <h2 className="fisa-penalty-notice-steps-orchestrator__title">
                                    Are you closing your account due to exceptional circumstances?
                                </h2>
                            </div>
                        
                        <div className="fisa-penalty-notice-steps-orchestrator__options">
                            <div className="fisa-penalty-notice-steps-orchestrator__option-group">
                                <div
                                    className={`fisa-penalty-notice-steps-orchestrator__radio-group ${selectedReason === 'exceptional' ? 'fisa-penalty-notice-steps-orchestrator__radio-group--selected' : ''}`}
                                    onClick={() => handleReasonChange('exceptional')}
                                >
                                    <div className="fisa-penalty-notice-steps-orchestrator__radio">
                                        <input
                                            type="radio"
                                            id="exceptional"
                                            name="exceptionalCircumstances"
                                            value="exceptional"
                                            checked={selectedReason === 'exceptional'}
                                            onChange={() => handleReasonChange('exceptional')}
                                        />
                                        <span className="fisa-penalty-notice-steps-orchestrator__radio-custom"></span>
                                    </div>
                                    <div className="fisa-penalty-notice-steps-orchestrator__radio-content">
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-label">
                                            Yes, I&apos;m closing my account due to exceptional circumstances
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-body">
                                            For example: a major life event such as illness, loss, or bankruptcy.
                                        </div>
                                    </div>
                                </div>
                                
                                <div
                                    className={`fisa-penalty-notice-steps-orchestrator__radio-group ${selectedReason === 'other' ? 'fisa-penalty-notice-steps-orchestrator__radio-group--selected' : ''}`}
                                    onClick={() => handleReasonChange('other')}
                                >
                                    <div className="fisa-penalty-notice-steps-orchestrator__radio">
                                        <input
                                            type="radio"
                                            id="other"
                                            name="exceptionalCircumstances"
                                            value="other"
                                            checked={selectedReason === 'other'}
                                            onChange={() => handleReasonChange('other')}
                                        />
                                        <span className="fisa-penalty-notice-steps-orchestrator__radio-custom"></span>
                                    </div>
                                    <div className="fisa-penalty-notice-steps-orchestrator__radio-content">
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-label">
                                            No, I&apos;m closing my account for another reason
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-body">
                                            For example: I&apos;ve changed my mind, switched products, no longer need the account, or want to consolidate my savings.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SUITS Support Section - Only show if "Yes" (exceptional circumstances) is selected */}
                    {selectedReason === 'exceptional' && (
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__suits-support">
                                <div className="fisa-penalty-notice-steps-orchestrator__suits-support-content">
                                    <div className="fisa-penalty-notice-steps-orchestrator__suits-support-header">
                                        <div className="fisa-penalty-notice-steps-orchestrator__suits-support-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                                                      stroke="#1463AA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__suits-support-text">
                                            <div className="fisa-penalty-notice-steps-orchestrator__suits-support-title">
                                                We&apos;re here to help
                                            </div>
                                            <div className="fisa-penalty-notice-steps-orchestrator__suits-support-body">
                                                We&apos;re sorry you&apos;re experiencing a tough time. If you&apos;d like support or have any questions, our team is here for you via live chat. We&apos;re available from 08:00-20:00 Monday to Friday, excluding bank holidays.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="fisa-penalty-notice-steps-orchestrator__suits-actions">
                                <Button
                                    size="med"
                                    variant="secondary"
                                    customClassNames={['fisa-cancel-button']}
                                    label="Cancel"
                                    onClick={handleCancel}
                                />
                                <Button
                                    size="med"
                                    label="Start Live chat"
                                    onClick={handleStartLiveChat}
                                />
                            </div>
                        </div>
                    )}

                    {/* Reason Selection Section - Only show if "No" is selected */}
                    {isOther && (
                        <div className="fisa-penalty-notice-steps-orchestrator__section fisa-penalty-notice-steps-orchestrator__section--reason-selection">
                            <div className="fisa-penalty-notice-steps-orchestrator__question">
                                <h2 className="fisa-penalty-notice-steps-orchestrator__title">
                                    To help us improve, please let us know your reason for closing
                                </h2>
                            </div>
                            
                            <div className="fisa-penalty-notice-steps-orchestrator__select-wrapper">
                                <Select
                                    value={closureReason}
                                    onChange={handleClosureReasonChange}
                                    options={closureReasonOptions}
                                    placeholder="Select a reason"
                                />
                                {showValidation && !closureReason && (
                                    <span className="fisa-penalty-notice-steps-orchestrator__validation-message">
                                        Please select a reason for closing your account
                                    </span>
                                )}
                            </div>

                            {/* Free text input for "Other" or "Unhappy with service" */}
                            {shouldShowFreeText && (
                                <div className="fisa-penalty-notice-steps-orchestrator__textarea-wrapper">
                                    <label htmlFor="freeTextReasonTextarea" className="fisa-penalty-notice-steps-orchestrator__textarea-label">
                                        Could you tell us more?
                                    </label>
                                    <textarea
                                        id="freeTextReasonTextarea"
                                        className="fisa-penalty-notice-steps-orchestrator__textarea"
                                        placeholder="Please provide more details..."
                                        value={freeTextReason}
                                        onChange={(e) => handleFreeTextChange(e.target.value)}
                                        rows={4}
                                    />
                                    {showValidation && !freeTextReason.trim() && (
                                        <div className="fisa-penalty-notice-steps-orchestrator__validation-message">
                                            Please add a few more details
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Payout Options Section - Only show if "No" is selected AND reason is chosen */}
                    {shouldShowPayoutOptions && (
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__question">
                                <h2 className="fisa-penalty-notice-steps-orchestrator__title">
                                    Where would you like your funds to be paid?
                                </h2>
                            </div>
                            <div className="fisa-penalty-notice-steps-orchestrator__options">
                                <div className="fisa-penalty-notice-steps-orchestrator__option-group">
                                    <div
                                        className={`fisa-penalty-notice-steps-orchestrator__radio-group ${payoutOption === 'linked' ? 'fisa-penalty-notice-steps-orchestrator__radio-group--selected' : ''}`}
                                        onClick={() => handlePayoutChange('linked')}
                                    >
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio">
                                            <input
                                                type="radio"
                                                id="linked"
                                                name="payoutOption"
                                                value="linked"
                                                checked={payoutOption === 'linked'}
                                                onChange={() => handlePayoutChange('linked')}
                                            />
                                            <span className="fisa-penalty-notice-steps-orchestrator__radio-custom"></span>
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-content">
                                            <div className="fisa-penalty-notice-steps-orchestrator__radio-label">
                                                To my linked current account
                                            </div>
                                            <div className="fisa-penalty-notice-steps-orchestrator__radio-body">
                                                We&apos;ll close your account and return your full balance, excluding the early closure fee, to your linked account. You&apos;ll lose the ISA tax-free status on the entire amount you withdraw.
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`fisa-penalty-notice-steps-orchestrator__radio-group ${payoutOption === 'transfer' ? 'fisa-penalty-notice-steps-orchestrator__radio-group--selected' : ''}`}
                                        onClick={() => handlePayoutChange('transfer')}
                                    >
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio">
                                            <input
                                                type="radio"
                                                id="transfer"
                                                name="payoutOption"
                                                value="transfer"
                                                checked={payoutOption === 'transfer'}
                                                onChange={() => handlePayoutChange('transfer')}
                                            />
                                            <span className="fisa-penalty-notice-steps-orchestrator__radio-custom"></span>
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__radio-content">
                                            <div className="fisa-penalty-notice-steps-orchestrator__radio-label">
                                                Transfer out to another ISA provider
                                            </div>
                                            <div className="fisa-penalty-notice-steps-orchestrator__radio-body">
                                                We&apos;ll transfer your balance to your new provider. Your ISA stays tax-free, and any interest earned to date will be paid into your linked account. An early closure fee applies.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Show checkbox only when Transfer out to another ISA provider is selected */}
                            {shouldShowPayoutOptions && payoutOption === 'transfer' && (
                                <div className="fisa-penalty-notice-steps-orchestrator__checkbox-payout">
                                    <div className="fisa-penalty-notice-steps-orchestrator__checkbox-wrapper">
                                        <div className="fisa-penalty-notice-steps-orchestrator__checkbox">
                                            <input 
                                                type="checkbox" 
                                                id="earlyClosureFeeAcknowledge" 
                                            checked={penaltyAcknowledged}
                                            onChange={(e) => {
                                                analytics.trackAnalyticsClickEvent(
                                                    `fisa penalty fee acknowledgment ${e.target.checked ? 'checked' : 'unchecked'}`,
                                                    window.location.href,
                                                    'fisa-penalty-closure',
                                                    'fisa_1yr',
                                                    '',
                                                    {},
                                                    'checkbox click',
                                                    `close_fisa_account|checkbox_click|fee_acknowledgment_${e.target.checked ? 'checked' : 'unchecked'}`,
                                                    'account_closure_penalty'
                                                );
                                                setPenaltyAcknowledged(e.target.checked);
                                            }}
                                            />
                                            <span className="fisa-penalty-notice-steps-orchestrator__checkbox-custom">
                                                {penaltyAcknowledged && (
                                                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 4.5L4.5 8L11 1" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </span>
                                        </div>
                                        <label htmlFor="earlyClosureFeeAcknowledge" className="fisa-penalty-notice-steps-orchestrator__checkbox-label">
                                            I understand I&apos;ll be charged an <strong>early closure fee</strong> of <strong>{/** @type {any} */(paymentDetails).formattedEarlyClosureFee}</strong> if I go ahead.
                                        </label>
                                    </div>
                                    {showValidation && payoutOption === 'transfer' && !penaltyAcknowledged && (
                                        <div className="fisa-penalty-notice-steps-orchestrator__validation-message">
                                            Please acknowledge the early closure fee to continue
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Validation message for payout options */}
                            {showValidation && !payoutOption && (
                                <div className="fisa-penalty-notice-steps-orchestrator__validation-message">
                                    Please select where you would like your funds to be paid
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action Buttons - Only show for "other" reason flow, not for exceptional circumstances */}
                    {selectedReason !== 'exceptional' && (
                        <div className="fisa-penalty-notice-steps-orchestrator__actions">
                            <Button
                                size="med"
                                variant="secondary"
                                customClassNames={['fisa-cancel-button']}
                                label="Cancel"
                                onClick={handleCancel}
                            />
                            {/* Only show Continue button when a radio option is selected */}
                            {selectedReason && (
                                <Button
                                    size="med"
                                    label="Continue"
                                    onClick={handleContinue}
                                    disabled={
                                        (isOther && !payoutOption) ||
                                        (isOther && payoutOption === 'transfer' && !penaltyAcknowledged)
                                    }
                                />
                            )}
                        </div>
                    )}
                    </div>
                )}

                {currentStep === 'confirmation' && (
                    <div className="fisa-penalty-notice-steps-orchestrator__content">
                        
                        {/* Important Information Section */}
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__question fisa-penalty-notice-steps-orchestrator__question--first">
                                <h2 className="fisa-penalty-notice-steps-orchestrator__title">
                                    Important information
                                </h2>
                            </div>
                        </div>

                        {/* Consequences Section */}
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__consequences">
                                <h3 className="fisa-penalty-notice-steps-orchestrator__subtitle">
                                    If you choose to go ahead:
                                </h3>
                                
                                <div className="fisa-penalty-notice-steps-orchestrator__consequence-list">
                                    <div className="fisa-penalty-notice-steps-orchestrator__consequence-item fisa-penalty-notice-steps-orchestrator__consequence-item--negative">
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-icon">
                                            <img src={ThumbsDownIcon} alt="Thumbs down" width="24" height="24" />
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-text">
                                            You&apos;ll lose the ISA tax benefits on the full amount you withdraw. For example, if you deposited £5,000 and choose to withdraw it, you&apos;ll lose the tax-free status on that entire amount.
                                        </div>
                                    </div>
                                    
                                    <div className="fisa-penalty-notice-steps-orchestrator__consequence-item fisa-penalty-notice-steps-orchestrator__consequence-item--negative">
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-icon">
                                            <img src={ThumbsDownIcon} alt="Thumbs down" width="24" height="24" />
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-text">
                                            If you withdraw before the end of the term, you will be charged <strong>an early closure fee equal to 90 days&apos;</strong> worth of interest based on your balance at that time.
                                        </div>
                                    </div>
                                    
                                    <div className="fisa-penalty-notice-steps-orchestrator__consequence-item fisa-penalty-notice-steps-orchestrator__consequence-item--positive">
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-icon">
                                            <img src={ThumbsUpIcon} alt="Thumbs up" width="24" height="24" />
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-text">
                                            Once your account is closed, you&apos;ll still be able to access your Investec Save dashboard to download your annual statements and interest certificates.
                                        </div>
                                    </div>
                                    
                                    <div className="fisa-penalty-notice-steps-orchestrator__consequence-item fisa-penalty-notice-steps-orchestrator__consequence-item--positive">
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-icon">
                                            <img src={ThumbsUpIcon} alt="Thumbs up" width="24" height="24" />
                                        </div>
                                        <div className="fisa-penalty-notice-steps-orchestrator__consequence-text">
                                            You can open another Fixed Rate Cash ISA with us in the future, if you choose to.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Final Payment Section */}
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__payment-box">
                                <h3 className="fisa-penalty-notice-steps-orchestrator__payment-title">
                                    Your final payment
                                </h3>
                                
                                <div className="fisa-penalty-notice-steps-orchestrator__payment-details">
                                    <div className="fisa-penalty-notice-steps-orchestrator__payment-row">
                                        <span className="fisa-penalty-notice-steps-orchestrator__payment-label">
                                            Current balance including interest earned to date
                                        </span>
                                        <span className="fisa-penalty-notice-steps-orchestrator__payment-amount">
                                            {/** @type {any} */(paymentDetails).formattedCurrentBalance}
                                        </span>
                                    </div>
                                    
                                    <div className="fisa-penalty-notice-steps-orchestrator__payment-row">
                                        <span className="fisa-penalty-notice-steps-orchestrator__payment-label">
                                            Early closure fee
                                        </span>
                                        <span className="fisa-penalty-notice-steps-orchestrator__payment-amount">
                                            {/** @type {any} */(paymentDetails).formattedEarlyClosureFee}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="fisa-penalty-notice-steps-orchestrator__payment-divider"></div>
                                
                                <div className="fisa-penalty-notice-steps-orchestrator__payment-row fisa-penalty-notice-steps-orchestrator__payment-row--final">
                                    <span className="fisa-penalty-notice-steps-orchestrator__payment-label">
                                        Final payment amount
                                    </span>
                                    <span className="fisa-penalty-notice-steps-orchestrator__payment-amount fisa-penalty-notice-steps-orchestrator__payment-amount--final">
                                        {/** @type {any} */(paymentDetails).formattedFinalPayment}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Confirmation Checkbox */}
                        <div className="fisa-penalty-notice-steps-orchestrator__section">
                            <div className="fisa-penalty-notice-steps-orchestrator__confirmation">
                                <div
                                    className={`fisa-penalty-notice-steps-orchestrator__checkbox-wrapper ${confirmationChecked ? 'fisa-penalty-notice-steps-orchestrator__checkbox-wrapper--checked' : ''}`}
                                >
                                    <div className="fisa-penalty-notice-steps-orchestrator__checkbox">
                                        <input
                                            type="checkbox"
                                            id="confirmation"
                                        checked={confirmationChecked}
                                        onChange={(e) => {
                                            analytics.trackAnalyticsClickEvent(
                                                `fisa penalty final confirmation ${e.target.checked ? 'checked' : 'unchecked'}`,
                                                window.location.href,
                                                'fisa-penalty-closure',
                                                'fisa_1yr',
                                                '',
                                                {},
                                                'checkbox click',
                                                `close_fisa_account|checkbox_click|final_confirmation_${e.target.checked ? 'checked' : 'unchecked'}`,
                                                'account_closure_penalty'
                                            );
                                            setConfirmationChecked(e.target.checked);
                                        }}
                                        />
                                        <span className="fisa-penalty-notice-steps-orchestrator__checkbox-custom">
                                            {confirmationChecked && (
                                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 4.5L4.5 8L11 1" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </span>
                                    </div>
                                    <label htmlFor="confirmation" className="fisa-penalty-notice-steps-orchestrator__checkbox-label">
                                        I understand I&apos;ll be charged an early closure fee if I go ahead.
                                    </label>
                                </div>
                                
                                {confirmationChecked && (
                                    <p className="fisa-penalty-notice-steps-orchestrator__confirmation-note">
                                        When you&apos;re ready, select &apos;Close my account&apos; to confirm.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Final Action Buttons */}
                        <div className="fisa-penalty-notice-steps-orchestrator__actions">
                            <Button
                                size="med"
                                label="Close my account"
                                onClick={handleFinalSubmit}
                                disabled={!confirmationChecked}
                            />
                            <Button
                                size="med"
                                variant="secondary"
                                customClassNames={['fisa-cancel-button']}
                                label="Don't close my account"
                                onClick={handleConfirmationBack}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {/* Transfer Instructions Modal */}
            <IsaTransferInstructionsModal
                isOpen={showTransferModal}
                onClose={handleTransferModalClose}
                onReturnToDashboard={handleTransferModalConfirm}
            />
        </div>
    );
}

export default FisaPenaltyNoticeStepsOrchestrator;
